import React, {useEffect, useState} from 'react';
import {
  wdiCore, WdiInput,
  WdiModalAppProps,
  WdiModalAppResult,
  WdiModalProps,
} from '@/components/WdiControls';
import {Button, DatePicker, Input, message, Popover, TableColumnsType, Typography} from 'antd';
import {WdiModal} from '@/components/WdiControls/WdiModalForm';
import {ResponseStatus} from '@/utils/request';
import {FilterColumn, GridFilterForm} from '@/components/GridWidget/GridFilterForm';
import {WdiTable} from '@/components/WdiControls/WdiTable';
import {TablePaginationConfig} from 'antd/lib/table';
import {SorterResult} from 'antd/lib/table/interface';
import {CloseOutlined} from '@ant-design/icons';
import {OA} from '@/services/oa';
import moment, {Moment} from 'moment';
import DataStore from '@/components/GridWidget/DataStore';

type TableParams = {
  pagination: TablePaginationConfig;
  sorter?: SorterResult<any> | null | undefined;
  filterOptions: any;
};

export type OaFormProps = {
  value?: API.OaForm;
  onChange?: (value?: API.OaForm) => void;
};

export const OaContractSelectApp: React.FC<OaFormProps> = (props) => {
  const [modalProps, setModalProps] = useState<WdiModalProps>({visible: false});
  const onClose = () => {
    setModalProps({visible: false, title: ""});
  };
  const onSave = (result?: WdiModalAppResult) => {
    setModalProps({visible: false, title: ""});
    if (result && result.code === ResponseStatus.SUCCESS) {
      props.onChange?.(result.data);
    }
  };

  const newProps: any = {
    onClick: () => {
      setModalProps({
        visible: true,
        title: '选择OA合同号',
        data: {
          records: props.value,
        },
      });
    },
    allowClear: {
      clearIcon: (
        <Button
          shape="circle"
          style={{border: 'none', width: 20, height: 20}}
          icon={<CloseOutlined/>}
          size="small"
          onClick={() => props.onChange?.()}
        />
      ),
    },
  };

  return (
    <>
      {modalProps.visible && (
        <OaContractSelectModalApp {...modalProps} onClose={onClose} onSave={onSave}/>
      )}
      <Popover
        content={wdiCore.String.Join(props.value?.oaFormNo, props.value?.oaFormName)}
      >
        <Input
          {...newProps}
          placeholder={'请选择OA合同号'}
          value={wdiCore.String.Join(props.value?.oaFormNo, props.value?.oaFormName)}
        />
      </Popover>
    </>
  );
}

type RangeValue = [Moment | null, Moment | null] | null;

const toOaForm = (contractVo: API.OaContractVo) => {
  return {
    oaFormNo: contractVo.contractNumber,
    oaFormName: contractVo.contractDesc,
    url: contractVo.url,
  }
};

const OaContractSelectModalApp: React.FC<WdiModalAppProps> = (props) => {
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<API.OaContractVo[]>([]);
  const [selectedRow, setSelectedRow] = useState<API.OaForm>();
  const [tableColumns, setTableColumns] = useState<any[]>([]);
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      defaultPageSize: 10,
      total: 0,
      showTotal: (total) => {
        return `共 ${total} 条`;
      },
      showSizeChanger: false
    },
    sorter: null,
    filterOptions: null,
  });

  useEffect(() => {
    (async () => {
      setTableColumns(await initTableColumns())
    })()
  }, []);

  // const setRowClass = (record: API.OaForm) => {
  //   return record.oaFormNo == selectedRow?.oaFormNo ? 'ant-table-row-selected' : ''
  // };

  const [dates, setDates] = useState<RangeValue>(null);
  const disabledDate = (current: Moment) => {
    if (!dates) {
      return false;
    }
    const tooLate = dates[0] && current.diff(dates[0], "day") > 365;
    const tooEarly = dates[0] && dates[0].diff(current, "day") > 365;
    return !!tooEarly || !!tooLate;
  };

  //OA合同编码（文本框），OA主题（文本框），OA申请日期（起止）
  const filterColumns: FilterColumn[] = [
    {
      title: 'OA合同编码',
      dataIndex: 'contractNumber',
      span: 4,
      render: () => {
        return <Input placeholder={"请输入OA合同编码"}/>;
      }
    },
    {
      title: 'OA合同名称',
      dataIndex: 'contractDesc',
      span: 4,
      render: () => {
        return <Input placeholder={"请输入OA主题"}/>;
      },
    },
    {
      title: 'OA申请日期',
      dataIndex: 'dateFrom',
      span: 8,
      render: () => {
        return <DatePicker.RangePicker
          style={{width: '100%'}}
          disabledDate={disabledDate}
          onCalendarChange={val => setDates(val)}
        />
      }
    },
  ];
  const initTableColumns = async () => {
    const columns: TableColumnsType<API.OaContractVo> = [
      {
        title: 'OA合同编码',
        dataIndex: 'contractNumber',
        ellipsis: true,
        width: 150,
        render: (_, record) => (
          <Typography.Link onClick={() => {
            window.open(record.url);
          }}>
            {record.contractNumber}
          </Typography.Link>
        )
      },
      {
        title: 'OA合同名称',
        width: 200,
        dataIndex: 'contractDesc',
        ellipsis: true,
      },
      {
        title: '合同类型',
        width: 200,
        dataIndex: 'contractTypeDesc',
        ellipsis: true,
      },
      {
        title: '合同金额',
        dataIndex: 'contractAmount',
        ellipsis: true,
        render: (_, record) => {
          return wdiCore.Number.AmountToString(record.contractAmount)
        },
      },
      {
        title: '申请人',
        width: 150,
        dataIndex: 'employeeName',
        ellipsis: true,
        render: (_, record) => {
          return wdiCore.String.CodeOrName(record.employeeCode, record.employeeName);
        },
      },
      {
        title: '申请日期',
        dataIndex: 'documentDate',
        ellipsis: true,
      },
    ];
    return columns
  }

  const getFilterOptions = (params: TableParams) => {
    const filterOptions: any[] = [];
    if (params.filterOptions) {
      const {contractNumber, contractDesc, dateFrom} = params.filterOptions;
      if (contractNumber) {
        filterOptions.push({
          title: 'OA合同编码',
          field: 'contractNumber',
          opt: 7,
          value: contractNumber
        })
      }
      if (contractDesc) {
        filterOptions.push({
          title: 'OA合同名称',
          field: 'contractDesc',
          opt: 7,
          value: contractDesc
        })
      }
      if (dateFrom?.[0]) {
        filterOptions.push({
          title: 'OA申请日期',
          field: 'dateFrom',
          opt: 4,
          value: moment(dateFrom[0]).format('YYYY-MM-DD')
        })
      }
      if (dateFrom?.[1]) {
        filterOptions.push({
          title: 'OA申请日期',
          field: 'dateFrom',
          opt: 6,
          value: moment(dateFrom[1]).format('YYYY-MM-DD')
        })
      }
      wdiCore.Object.RemoveNullOrUndefined(filterOptions);
    }
    return filterOptions;
  }

  const fetchData = (params: TableParams) => {
    setLoading(true);
    const filterOptions = getFilterOptions(params);
    const req: any = {
      filterOptions: JSON.stringify(filterOptions),
      id: 'OaContractSelect',
      pagenum: params.pagination.current ? (params.pagination.current - 1) : 0,
      pagesize: params.pagination.pageSize ? params.pagination.pageSize : 10,
      sortdatafield: 'contractNumber',
      sortorder: 'DESC'
    };
    OA.queryContract(req).then(res => {
      setDataSource(DataStore.init(res?.data?.records));
      setTableParams({
        pagination: {
          ...params.pagination,
          total: res?.data?.totalCount ?? 0,
        },
        sorter: params.sorter,
        filterOptions: params.filterOptions
      });
      setLoading(false);
    });
  };
  const doSearch = (filterOptions: any) => {
    const {contractNumber, contractDesc} = filterOptions;
    if (!contractNumber && !contractDesc) {
      message.error("OA合同编码/合同名称要至少输入一个").then();
      return
    }
    fetchData(Object.assign({}, tableParams, {filterOptions}));
  };

  const onSelectChange = (record: API.OaProgramVo, selected: boolean) => {
    if (selected) {
      setSelectedRow(toOaForm(record));
    } else {
      setSelectedRow(undefined);
    }
  };

  const doSave = () => {
    if (!selectedRow) {
      message.error('请选择一条数据').then();
      return false;
    }

    props.onSave?.({code: ResponseStatus.SUCCESS, data: selectedRow});
    return true;
  };

  return (
    <WdiModal
      title={props.title}
      visible={props.visible}
      width={1200}
      bodyStyle={{height: 600}}
      onCancel={() => {
        props.onClose?.()
      }}
      onOk={doSave}
    >
      <GridFilterForm doSearch={doSearch} columns={filterColumns}/>
      <WdiTable
        rowSelection={{
          selectedRowKeys: selectedRow?.oaFormNo ? [selectedRow?.oaFormNo] : [],
          onSelect: onSelectChange,
        }}
        rowKey="contractNumber"
        columns={tableColumns}
        showPageRows={10}
        dataSource={dataSource}
        pagination={{
          ...tableParams.pagination,
          onChange: (page, pageSize) => {
            fetchData(Object.assign({}, tableParams, {
              pagination: {
                ...tableParams.pagination,
                current: page,
                pageSize: pageSize
              }
            }))
          }
        }}
        loading={loading}
        // rowClassName={setRowClass}
        onRow={(record: API.OaContractVo) => {
          return {
            onClick: () => {
              setSelectedRow(toOaForm(record));
            },
            onDoubleClick: () => {
              setSelectedRow(toOaForm(record));
              doSave();
            },
          }
        }}
      />
    </WdiModal>
  );
};

export const OaFormApp: React.FC<OaFormProps> = (props) => {
  const handleInputClick = () => {
    if (props.value?.url) {
      window.open(props.value.url, '_blank');
    }
  };
  return (
    <>
      <WdiInput
        style={{color: props.value?.url ? '#2299ee' : '', cursor: 'pointer'}}
        readOnly
        value={wdiCore.String.Join(props.value?.oaFormNo, props.value?.oaFormName)}
        onClick={handleInputClick}
      />
    </>
  );
};
